import { useCallback } from "react";

import useAuth from "$src/core/hooks/use-auth";
import useSubscriptionCancelMutation from "$src/core/mutations/use-subscription-cancel-mutation";
import useSubscriptionOfferActivateMutation from "$src/core/mutations/use-subscription-offer-activate-mutation";
import useSubscriptionOfferSwitchMutation from "$src/core/mutations/use-subscription-offer-switch-mutation";
import useSubscriptionOffersQuery from "$src/core/queries/use-subscription-offers-query";
import useSubscriptionQuery from "$src/core/queries/use-subscription-query";
import combineStatuses from "$src/core/utils/query/combine-statuses";

const useSubscription = () => {
  const { status: authStatus } = useAuth();

  const { data: offers, status: offersStatus } = useSubscriptionOffersQuery({
    enabled: authStatus === "authenticated",
    select: (data) => [...data].sort((a, b) => a.price.amount - b.price.amount),
  });
  const { data: subscription, status: subscriptionsStatus } =
    useSubscriptionQuery({
      enabled: authStatus === "authenticated",
    });

  const { mutateAsync: mutateActivateSubscriptionOffer } =
    useSubscriptionOfferActivateMutation();
  const { mutateAsync: mutateSwitchSubscriptionOffer } =
    useSubscriptionOfferSwitchMutation();
  const { mutateAsync: mutateCancelSubscription } =
    useSubscriptionCancelMutation();

  const activateSubscriptionOffer = useCallback(
    async (
      offerId: string,
      {
        action,
        coupon,
      }: { action?: "upgrade" | "downgrade"; coupon?: string } = {},
    ) => {
      if (action === "upgrade" || action === "downgrade") {
        const currentOffer = offers?.find(
          (offer) => offer.id === subscription?.offerId,
        );
        if (!currentOffer) {
          throw new Error("Current offer not found");
        }
        await mutateSwitchSubscriptionOffer({
          type: action,
          offerId: currentOffer.id,
          toOfferId: offerId,
          coupon: coupon ?? "100OFF", // TODO: We don't have checkout, so we use this coupon for now
        });
      } else {
        await mutateActivateSubscriptionOffer({
          offerId,
          coupon: coupon ?? "100OFF", // TODO: We don't have checkout, so we use this coupon for now
        });
      }
    },
    [
      mutateActivateSubscriptionOffer,
      mutateSwitchSubscriptionOffer,
      offers,
      subscription?.offerId,
    ],
  );

  const cancelSubscription = useCallback(
    async (id: string) => mutateCancelSubscription({ id }),
    [mutateCancelSubscription],
  );

  const status = combineStatuses(offersStatus, subscriptionsStatus);

  return {
    status,
    offers,
    subscription,
    activateSubscriptionOffer,
    cancelSubscription,
  };
};

export default useSubscription;
